import { pick, omit } from 'lodash';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';
import { carrierKeys, documentKeys, termsKeys, xedmKeys } from 'store/utils';

import { currencyFormatter } from 'utils';
import { percentageFormatter } from 'utils';

export const downloadFile = ({ response, file }) => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${file.name}`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export const downloadRequestHeaders = {
  responseType: 'arraybuffer',
  headers: {
    'Content-Type': 'application/json',
    Accept: `application/pdf`,
  },
};

// function calculateBusinessPlanDetails
// this function creates and initializes businessPlanDetails object from payload,
// we also use this function to get the list of properties in businessPlanDetails by
// calling it without a payload, e.g. Object.keys(calculateBusinessPlanDetails()),
// for this reason we first check if payload exists by using optional chaining operator (?.)
// and isNil()

export const calculateRestDetails = payload => {
  return {
    companySize: payload?.companySize,
    companyLegalType: payload?.companyLegalType,
    eligibilityCheck: payload?.eligibilityCheck,
    investmentPlanElements: payload?.investmentPlanElements,
  };
};

export const euroFormatter = new Intl.NumberFormat('el-GR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
});

export const printInfo = info => {
  if (typeof info === 'undefined' || info === null || info.length === 0) {
    return '-';
  } else {
    return info;
  }
};

export const printValue = info => {
  if (typeof info === 'undefined' || info === null || info.length === 0) {
    return '-';
  } else {
    return info + ' €';
  }
};

export const calculateBusinessPlanOverviewTables = payload => {
  return {
    InvestmentTotalAmountTable:
      isNil(payload) || isNil(payload.businessPlanDetails.businessPlanAssets)
        ? []
        : [
            {
              title: 'Ιδία Συμμετοχή',
              percentage: percentageFormatter(
                ((sumBy(
                  payload.businessPlanDetails.businessPlanAssets,
                  'totalPrice'
                ) -
                  sumBy(
                    payload.businessPlanDetails.businessPlanAssets,
                    'publicExp'
                  )) /
                  sumBy(
                    payload.businessPlanDetails.businessPlanAssets,
                    'totalPrice'
                  )) *
                  100
              ),
              SumTotalExpense: currencyFormatter(
                sumBy(
                  payload.businessPlanDetails.businessPlanAssets,
                  'totalPrice'
                ) -
                  sumBy(
                    payload.businessPlanDetails.businessPlanAssets,
                    'publicExp'
                  )
              ),
            },
            {
              title: 'Δημόσια Δαπάνη',
              percentage: percentageFormatter(
                (sumBy(
                  payload.businessPlanDetails.businessPlanAssets,
                  'publicExp'
                ) /
                  sumBy(
                    payload.businessPlanDetails.businessPlanAssets,
                    'totalPrice'
                  )) *
                  100
              ),
              SumTotalExpense: currencyFormatter(
                sumBy(
                  payload.businessPlanDetails.businessPlanAssets,
                  'publicExp'
                )
              ),
            },
            {
              title: 'Σύνολο',
              percentage:
                sumBy(
                  payload.businessPlanDetails.businessPlanAssets,
                  'totalPrice'
                ) == 0
                  ? '0,00 %'
                  : '100,00 %',
              SumTotalExpense: currencyFormatter(
                sumBy(
                  payload.businessPlanDetails.businessPlanAssets,
                  'totalPrice'
                )
              ),
            },
          ],
  };
};

export function mapMainformDetails(apiData, currentState) {
  const mappedDocuments = (apiData.documents || []).reduce((acc, doc) => {
    if (doc.field) acc[doc.field] = doc;
    return acc;
  }, {});

  const stateData = {
    ...currentState,
    documents: {
      ...mappedDocuments,
    },
    carrier: {
      ...currentState.carrier,
      // TAB1.1
      companyName: apiData.companyDetails.companyName,
      abiGcrCode: apiData.companyDetails.gemiNumber,
      legalVat: apiData.companyDetails.taxId,
      companyLegalType: apiData.companyDetails.legalForm,
      businessSize: apiData.companyDetails.companySize,
      abiDistrict: apiData.companyDetails.municipality,
      buildStreet: apiData.companyDetails.streetAddress,
      buildTk: apiData.companyDetails.postalCode,
      legalPhoneMobile: apiData.companyDetails.contactPhone,
      legalEmail: apiData.companyDetails.contactEmail,

      // TAB4
      bnkBank: apiData.bank,

      // TAB3.1
      dapanesEpendysis: JSON.stringify({
        ependutikoSxedio: {
          titlos: apiData.investmentPlan.investmentPlanDetails.title,
          kwdikosKad:
            apiData.investmentPlan.investmentPlanDetails.activityDescription,
          enarksh: apiData.investmentPlan.investmentPlanDetails.startedOn,
          oloklhrwsh: apiData.investmentPlan.investmentPlanDetails.completedOn,
          perigrafi: apiData.investmentPlan.investmentPlanDetails.description,
        },
        toposEkteleshs: {
          periferia: apiData.investmentPlan.investmentPlanDetails.region,
          periferiakiEnotita:
            apiData.investmentPlan.investmentPlanDetails.regionalUnit,
          dimos: apiData.investmentPlan.investmentPlanDetails.municipality,
          topos: apiData.investmentPlan.investmentPlanDetails.location,
        },
        //TAB3.2 funding scheme

        // TAB3.3 expenditures
        proupologismosEpendytikouSxediou:
          apiData.investmentPlan.expenditures.map(exp => ({
            ependytikiDapani: exp.title,
            perigrafiEpendytikisDapanis: exp.description,
            kostosAgoras: `${exp.acquisitionCost}`,
          })),
      }),
      fundingScheme: {
        ...apiData.fundingScheme,
      },
      fundingPrograms: [...apiData.fundingPrograms],

      // TAB3.4
      activityField:
        apiData.investmentPlan.revenues?.activityField === 'TOURISM'
          ? 'tourist'
          : apiData.investmentPlan.revenues?.activityField,
      operatingMonths: apiData.investmentPlan.revenues?.operatingMonths,
      roomsNum: apiData.investmentPlan.revenues?.roomsNum,
      occupancyRate: apiData.investmentPlan.revenues?.occupancyRate,
      avgRoomDailyRate: apiData.investmentPlan.revenues?.avgRoomDailyRate,
      estimatedQuantity: apiData.investmentPlan.revenues?.estimatedQuantity,
      avgSellingPrice: apiData.investmentPlan.revenues?.avgSellingPrice,
    },
    task: {
      ...currentState.task,
      processInstanceId:
        apiData?.processInstanceId ||
        currentState.forms?.task?.processInstanceId,
      taskName: apiData?.activeTaskName || currentState.forms?.task?.taskName,
      taskKey: apiData?.taskKey || currentState.forms?.task?.taskKey,
    },
  };
  return stateData;
}

export function mapMainformUpdate(stateData, bestFitResultId) {
  const carrier = stateData.carrier; // Eimai idi sta forms; aka; stateData ==> state.forms from useSelector
  const documents = stateData.documents; // Eimai idi sta forms; aka; stateData ==> state.forms from useSelector
  const terms = stateData.terms; // Eimai idi sta forms; aka; stateData ==> state.forms from useSelector

  const apiData = {
    bestFitResultId, // from uri params
    gemiNumber: carrier.abiGcrCode,
    shareholders: (carrier?.carrierUsers ?? []).map(user => ({
      fullName: user.shareholderName,
      taxId: user.shareholderVat,
      country: user.shareholderCountry,
      participationPercentage: user.shareholderPercentage,
    })),
    beneficialOwners: carrier.beneficialOwners,
    documents: expandDocuments(documents),
    investmentPlan: {
      revenues: {
        activityField:
          carrier.activityField === 'tourist' ? 'TOURISM' : 'OTHER',
        operatingMonths: carrier.operatingMonths,
        roomsNum: carrier.roomsNum,
        occupancyRate: carrier.occupancyRate,
        estimatedQuantity: carrier.estimatedQuantity,
        avgRoomDailyRate: carrier.avgRoomDailyRate,
        avgSellingPrice: carrier.avgSellingPrice,
      },
    },
    bank: carrier.bnkBank,
    terms: omit(terms, ['termsAll']),
  };
  return apiData;
}

function expandDocuments(documents) {
  const expanded = Object.entries(documents)
    .filter(([key]) => key !== 'investmentExpenditures')
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return {
          [key]: value.map(doc => ({ id: doc.contentItemId })),
        };
      }
      return {
        [value.field]: {
          id: value.contentItemId,
        },
      };
    });

  return Object.assign({}, ...expanded);
}
